// AdventurerIllustration — renders a baked PNG of the adventurer in a
// specific pose, falling back to the procedural AdventurerStamp when the
// PNG hasn't been generated yet. Keeps the app functional during the
// (multi-batch) image-baking rollout.

import { useEffect, useRef, useState } from 'react';
import AdventurerStamp, { type AdventurerId } from './AdventurerStamp';
import { adventurerAssetUrl, poseForBeat, type AdventurerPose, type Beat } from '../../assets/illustrations';

export type { Beat };

interface Props {
  id: AdventurerId;
  /** Semantic beat — mapped to a pose internally. */
  beat?: Beat;
  /** Explicit pose override. Takes precedence over `beat` when set. */
  pose?: AdventurerPose;
  size?: number;
  /** Show the "Zurück"-style label below. Passed through to stamp fallback. */
  showLabel?: boolean;
}

export default function AdventurerIllustration({
  id, beat = 'idle', pose, size = 128, showLabel = false,
}: Props) {
  const effectivePose: AdventurerPose = pose ?? poseForBeat(beat);
  const [status, setStatus] = useState<'pending' | 'ok' | 'fallback'>('pending');
  const [animKey, setAnimKey] = useState(0);
  const prevPoseRef = useRef<AdventurerPose>(effectivePose);

  const url = adventurerAssetUrl(id, effectivePose);

  // Probe the asset on mount and whenever the resolved URL changes.
  useEffect(() => {
    let cancelled = false;
    setStatus('pending');
    const img = new Image();
    img.onload  = () => { if (!cancelled) setStatus('ok'); };
    img.onerror = () => { if (!cancelled) setStatus('fallback'); };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);

  // Bump the animation key whenever the pose actually changes so the
  // CSS keyframe replays (not on every render).
  useEffect(() => {
    if (prevPoseRef.current !== effectivePose) {
      prevPoseRef.current = effectivePose;
      setAnimKey(k => k + 1);
    }
  }, [effectivePose]);

  if (status === 'fallback') {
    return <AdventurerStamp id={id} size={size} showLabel={showLabel} />;
  }

  // Pick an animation class per beat so "excited" bounces, "thinking" tilts,
  // etc. Classes live in App.css (keyframes added alongside this component).
  const animClass =
    effectivePose === 'excited' || effectivePose === 'celebrating' ? 'adv-bounce'
    : effectivePose === 'thinking' || effectivePose === 'confused' ? 'adv-tilt'
    : effectivePose === 'encouraging' || effectivePose === 'reading' ? 'adv-nod'
    : 'adv-idle';

  return (
    <div style={{
      width: size, height: size,
      position: 'relative',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img
        key={`${id}-${effectivePose}-${animKey}`}
        src={url}
        alt=""
        width={size}
        height={size}
        className={`adv-illus ${animClass}`}
        style={{
          width: size, height: size,
          objectFit: 'contain',
          opacity: status === 'ok' ? 1 : 0,
          transition: 'opacity 220ms ease',
        }}
      />
    </div>
  );
}
